import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tournamentsService, matchesService, leaderboardService, playersService, predictionsService } from '../../services/supabaseService';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import TournamentHeader from './components/TournamentHeader';
import OverviewTab from './components/OverviewTab';
import PredictionsTab from './components/PredictionsTab';
import ScheduleTab from './components/ScheduleTab';
import LeaderboardTab from './components/LeaderboardTab';
import Icon from '../../components/AppIcon';

const TournamentDetails = () => {
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  
  const [activeTab, setActiveTab] = useState('schedule');
  const [isParticipating, setIsParticipating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchPredictions, setMatchPredictions] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);

  useEffect(() => {
    if (tournamentId) {
      fetchTournamentData();
    } else {
      setError('No tournament selected');
      setLoading(false);
    }
  }, [tournamentId]);

  // Real-time leaderboard subscription
  useEffect(() => {
    if (!tournamentId) return;

    const subscription = leaderboardService?.subscribeToLeaderboard(
      tournamentId,
      (updatedLeaderboard) => {
        // Transform updated leaderboard data
        const transformedLeaderboard = updatedLeaderboard?.map(entry => ({
          id: entry?.userId,
          rank: entry?.rank,
          name: entry?.userProfiles?.fullName || entry?.userProfiles?.email,
          email: entry?.userProfiles?.email,
          avatar: entry?.userProfiles?.avatarUrl || 'https://via.placeholder.com/150',
          avatarAlt: `${entry?.userProfiles?.fullName || 'User'} avatar`,
          totalPoints: entry?.totalPoints,
          correctPredictions: entry?.correctPredictions,
          totalPredictions: entry?.totalPredictions,
          accuracy: entry?.accuracy ? parseFloat(entry?.accuracy)?.toFixed(1) : '0.0',
          pointsBreakdown: {
            tournamentPredictions: entry?.tournamentPredictionPoints,
            matchPredictionPoints: entry?.matchPredictionPoints,
            bonusPoints: entry?.bonusPoints,
            confidenceBonus: entry?.confidenceBonus
          }
        })) || [];

        setLeaderboard(transformedLeaderboard);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, [tournamentId]);

  const fetchTournamentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [tournamentData, matchesData, leaderboardData, playersData] = await Promise.all([
        tournamentsService?.getById(tournamentId),
        matchesService?.getByTournament(tournamentId),
        leaderboardService?.getByTournament(tournamentId),
        playersService?.getByTournament(tournamentId)
      ]);

      if (!tournamentData) {
        setError('Tournament not found');
        setLoading(false);
        return;
      }

      // Transform tournament data
      const transformedTournament = {
        id: tournamentData?.id,
        name: tournamentData?.name,
        format: tournamentData?.format,
        matchType: tournamentData?.matchType,
        startDate: tournamentData?.startDate,
        endDate: tournamentData?.endDate,
        location: tournamentData?.location,
        bannerImage: tournamentData?.bannerImage || 'https://images.unsplash.com/photo-1730739628981-6537b299aea3',
        bannerImageAlt: tournamentData?.bannerImageAlt || 'Tournament banner',
        prize1: tournamentData?.prize1,
        prize2: tournamentData?.prize2,
        prize3: tournamentData?.prize3,
        participants: tournamentData?.tournamentParticipants?.length || 0,
        totalMatches: matchesData?.length || 0,
        status: tournamentData?.status,
        predictionLockDate: tournamentData?.predictionLockDate,
        description: tournamentData?.description,
        predictionSettings: tournamentData?.predictionSettings,
        enableConfidenceSlider: tournamentData?.enableConfidenceSlider,
        teams: tournamentData?.tournamentTeams?.map(tt => ({
          id: tt?.teams?.id,
          name: tt?.teams?.name,
          shortName: tt?.teams?.shortName || tt?.teams?.name?.substring(0, 3)?.toUpperCase(),
          country: tt?.teams?.country,
          logo: tt?.teams?.logo,
          logoAlt: tt?.teams?.logoAlt
        })) || [],
        players: playersData?.map(player => {
          return {
            id: player?.id,
            name: player?.name,
            team: player?.teamShortName || player?.teamName?.substring(0, 3)?.toUpperCase(),
            role: player?.role,
            teamId: player?.teamId
          };
        }) || [],
        pointsSystem: tournamentData?.pointsSystem?.map(ps => ({
          category: ps?.category,
          points: ps?.points,
          description: ps?.description
        })) || []
      };

      // Set participation status from fetched data
      setIsParticipating(tournamentData?.isParticipating || false);

      // Transform matches
      const transformedMatches = matchesData?.map(match => ({
        id: match?.id,
        matchType: match?.matchType,
        stage: match?.stage,
        dateTime: match?.matchDate,
        venue: match?.venue,
        team1: {
          name: match?.team1?.name,
          logo: match?.team1?.logo,
          logoAlt: match?.team1?.logoAlt,
          score: match?.team1Score,
          wickets: match?.team1Wickets,
          id: match?.team1Id
        },
        team2: {
          name: match?.team2?.name,
          logo: match?.team2?.logo,
          logoAlt: match?.team2?.logoAlt,
          score: match?.team2Score,
          wickets: match?.team2Wickets,
          id: match?.team2Id
        },
        status: match?.status,
        winnerId: match?.winnerId,
        tossWinnerId: match?.tossWinnerId,
        battingFirstId: match?.battingFirstId,
        manOfMatchId: match?.manOfMatchId,
        highestScorerId: match?.highestScorerId,
        highestWicketTakerId: match?.highestWicketTakerId,
        predictionsEnabled: match?.predictionsEnabled ?? false
      })) || [];

      // Transform leaderboard
      const transformedLeaderboard = leaderboardData?.map(entry => ({
        id: entry?.userId,
        rank: entry?.rank,
        name: entry?.userProfiles?.fullName || entry?.userProfiles?.email,
        email: entry?.userProfiles?.email,
        avatar: entry?.userProfiles?.avatarUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_178d491d9-1763295824835.png',
        avatarAlt: `Profile picture of ${entry?.userProfiles?.fullName || 'user'}`,
        totalPoints: entry?.totalPoints,
        correctPredictions: entry?.correctPredictions,
        totalPredictions: entry?.totalPredictions,
        accuracy: entry?.accuracy,
        pointsBreakdown: {
          tournamentPredictions: entry?.tournamentPredictionPoints,
          matchPredictionPoints: entry?.matchPredictionPoints,
          bonusPoints: entry?.bonusPoints,
          confidenceBonus: entry?.confidenceBonus
        }
      })) || [];

      setTournament(transformedTournament);
      setMatches(transformedMatches);
      setLeaderboard(transformedLeaderboard);

      // Fetch match predictions for all matches
      await fetchMatchPredictions(transformedMatches);
    } catch (err) {
      console.error('Error fetching tournament data:', err);
      setError('Failed to load tournament data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchPredictions = async (matchesList) => {
    try {
      const predictions = {};

      // Fetch predictions for all matches in parallel
      // Use allSettled so a single failure doesn't wipe out all predictions
      const predictionPromises = matchesList?.map(async (match) => {
        try {
          const prediction = await predictionsService?.getMatchPrediction(match?.id);
          return { matchId: match?.id, prediction: prediction || null };
        } catch (err) {
          console.warn(`Failed to fetch prediction for match ${match?.id}:`, err?.message);
          return { matchId: match?.id, prediction: null };
        }
      });

      const results = await Promise.allSettled(predictionPromises);

      // Convert array to object for easy lookup - store full prediction data
      results?.forEach(result => {
        if (result?.status === 'fulfilled' && result?.value) {
          predictions[result?.value?.matchId] = result?.value?.prediction;
        }
      });

      setMatchPredictions(predictions);
    } catch (err) {
      console.error('Error fetching match predictions:', err);
      // Don't fail the entire page if predictions fetch fails
    }
  };

  const handleJoinTournament = async () => {
    try {
      await tournamentsService?.joinTournament(tournamentId);
      setIsParticipating(true);
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Error joining tournament:', err);
      setToastMsg({ message: 'Failed to join tournament. Please try again.', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  const handleLeaveTournament = async () => {
    try {
      await tournamentsService?.leaveTournament(tournamentId);
      setIsParticipating(false);
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Error leaving tournament:', err);
      setToastMsg({ message: 'Failed to leave tournament. Please try again.', type: 'error' });
      setTimeout(() => setToastMsg(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading tournament...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Tournament not found'}</p>
            <button
              onClick={() => navigate('/user-dashboard')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mockTournament = tournament;
  const mockMatches = matches;
  const mockLeaderboard = leaderboard;

  const tabs = [
  { id: 'schedule', label: 'Schedule', icon: 'Calendar' },
  { id: 'predictions', label: 'Predictions', icon: 'Target' },
  { id: 'leaderboard', label: 'Leaderboard', icon: 'TrendingUp' },
  { id: 'overview', label: 'Overview', icon: 'Info' }];


  const handleJoinLeave = (joining) => {
    if (joining) {
      handleJoinTournament();
    } else {
      setShowConfirmModal(true);
    }
  };

  const confirmLeave = async () => {
    try {
      await handleLeaveTournament();
    } catch (err) {
      console.error('Error leaving tournament:', err);
    }
  };

  const handleSubmitPredictions = async (predictions, setIsSubmitting, setToast, setErrorModal) => {
    try {
      if (setIsSubmitting) setIsSubmitting(true);
      console.log('Submitting predictions:', predictions);

      // Auto-join tournament if not already participating
      if (!isParticipating) {
        await tournamentsService?.joinTournament(tournamentId);
        setIsParticipating(true);
      }

      // Save predictions to database
      await predictionsService?.saveTournamentPrediction(tournamentId, {
        championTeamId: predictions?.champion,
        championConfidence: predictions?.championConfidence,
        semifinalist1Id: predictions?.semifinalists?.[0],
        semifinalist2Id: predictions?.semifinalists?.[1],
        semifinalist3Id: predictions?.semifinalists?.[2],
        semifinalist4Id: predictions?.semifinalists?.[3],
        semifinalistsConfidence: predictions?.semifinalistsConfidence,
        finalist1Id: predictions?.finalists?.[0],
        finalist2Id: predictions?.finalists?.[1],
        finalistsConfidence: predictions?.finalistsConfidence,
        playerOfTournamentId: predictions?.playerOfTournament,
        playerConfidence: predictions?.playerConfidence,
        top4Team1Id: predictions?.top4Teams?.[0] || null,
        top4Team2Id: predictions?.top4Teams?.[1] || null,
        top4Team3Id: predictions?.top4Teams?.[2] || null,
        top4Team4Id: predictions?.top4Teams?.[3] || null,
        top4Confidence: predictions?.top4Confidence,
        superOverTeamId: predictions?.superOverTeam || null,
        superOverConfidence: predictions?.superOverConfidence
      });

      if (setToast) setToast({ message: 'Predictions submitted successfully! You can modify them until the deadline.', type: 'success' });
    } catch (err) {
      console.error('Error submitting predictions:', err);
      if (setErrorModal) setErrorModal({ title: 'Submission Failed', message: err?.message || 'Failed to submit predictions. Please try again.' });
    } finally {
      if (setIsSubmitting) setIsSubmitting(false);
    }
  };

  const handleNavigateToMatch = (matchId) => {
    navigate(`/match-predictions/${matchId}`, { state: { tournamentId: mockTournament?.id } });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb tournamentName={mockTournament?.name} />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6">
          <TournamentHeader
            tournament={mockTournament}
            isParticipating={isParticipating}
            onJoinLeave={handleJoinLeave}
            onViewPredictions={() => setActiveTab('predictions')} />


          <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex min-w-max">
                {tabs?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors flex-shrink-0 ${
                  activeTab === tab?.id ?
                  'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`
                  }>

                    <Icon name={tab?.icon} size={20} />
                    <span>{tab?.label}</span>
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              {activeTab === 'overview' && <OverviewTab tournament={mockTournament} />}
              {activeTab === 'predictions' &&
              <PredictionsTab
                tournament={mockTournament}
                onSubmitPredictions={handleSubmitPredictions} />

              }
              {activeTab === 'schedule' &&
              <ScheduleTab
                matches={mockMatches}
                tournament={mockTournament}
                matchPredictions={matchPredictions}
                onNavigateToMatch={handleNavigateToMatch} />

              }
              {activeTab === 'leaderboard' && <LeaderboardTab leaderboard={mockLeaderboard} tournament={mockTournament} />}
            </div>
          </div>
        </div>
      </main>
      {showConfirmModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-elevation-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Leave Tournament?</h3>
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              Are you sure you want to leave this tournament? All your predictions will be removed and you won't be eligible for prizes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
              onClick={() => setShowConfirmModal(false)}
              className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-colors">

                Cancel
              </button>
              <button
              onClick={confirmLeave}
              className="flex-1 px-4 py-2 bg-error text-error-foreground rounded-lg font-medium hover:bg-error/90 transition-colors">

                Leave Tournament
              </button>
            </div>
          </div>
        </div>
      }
      {toastMsg && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in fade-in slide-in-from-bottom-4 ${toastMsg?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-success text-success-foreground'}`}>
          {toastMsg?.message}
        </div>
      )}
    </div>);

};

export default TournamentDetails;